import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import BookingForm from '../components/BookingForm';
import { Helmet } from 'react-helmet-async';
import './MusicianProfile.css';

function extractYouTubeId(url) {
  const regex = /(?:\?v=|\/embed\/|\.be\/|\/shorts\/)([a-zA-Z0-9_-]{11})/;
  const match = url.match(regex);
  return match ? match[1] : '';
}

export default function MusicianProfile() {
 const { id } = useParams();
 const [musician, setMusician] = useState(null);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const navigate = useNavigate();

 const formatGenresForSEO = (genres) => {
   if (!genres || !Array.isArray(genres) || genres.length === 0) return 'Artiste';
   return genres.join(', ');
 };

 const formatGenresForTitle = (genres) => {
   if (!genres || !Array.isArray(genres) || genres.length === 0) return 'Artiste';
   if (genres.length === 1) return genres[0];
   if (genres.length === 2) return genres.join(' & ');
   return `${genres[0]} & ${genres.length - 1} autres`;
 };

 useEffect(() => {
   const fetchMusician = async () => {
     try {
       const docRef = doc(db, 'Musiciens', id);
       const docSnap = await getDoc(docRef);
       if (docSnap.exists()) {
         setMusician({ id: docSnap.id, ...docSnap.data() });
       } else {
         setError('404');
       }
     } catch (err) {
       setError('error');
     }
     setLoading(false);
   };

   fetchMusician();
 }, [id]);

 if (loading) {
   return <p style={{ color: 'white', textAlign: 'center' }}>Chargement...</p>;
 }

 if (error === '404') {
   return (
     <div style={{ color: 'white', textAlign: 'center', padding: '2rem' }}>
       <Helmet>
         <title>Artiste non trouvé | Gates of Music</title>
         <meta name="robots" content="noindex" />
       </Helmet>
       <h1>Artiste non trouvé</h1>
       <p>Ce profil n'existe pas ou a été supprimé.</p>
       <button onClick={() => navigate('/')}>Retour à l'accueil</button>
     </div>
   );
 }

 if (error) {
   return (
     <div style={{ color: 'white', textAlign: 'center', padding: '2rem' }}>
       <Helmet>
         <title>Erreur | Gates of Music</title>
         <meta name="robots" content="noindex" />
       </Helmet>
       <h1>Erreur de chargement</h1>
       <p>Une erreur est survenue lors du chargement du profil.</p>
       <button onClick={() => navigate('/')}>Retour à l'accueil</button>
     </div>
   );
 }

 const genresForTitle = formatGenresForTitle(musician.genres);
 const genresForSEO = formatGenresForSEO(musician.genres);
 const absoluteImageURL = musician.imageURL?.startsWith('http')
  ? musician.imageURL
  : `https://www.gatesofmusic.com${musician.imageURL}`;

 return (
   <article className="musician-profile-container animated-fade-in">
     <Helmet>
       <title>{`${musician.name} - Artiste ${genresForTitle} à ${musician.city} | Gates of Music`}</title>
       <meta
         name="description"
         content={`Réservez ${musician.name}, artiste ${genresForSEO} professionnel basé à ${musician.city}. ${musician.bio?.substring(0, 120)}...`}
       />
       <link rel="canonical" href={`https://www.gatesofmusic.com/musicians/${musician.id}`} />

       {/* Open Graph */}
       <meta property="og:title" content={`${musician.name} - Artiste ${genresForTitle} | Gates of Music`} />
       <meta
         property="og:description"
         content={`Réservez ${musician.name}, artiste ${genresForSEO} à ${musician.city}. ${musician.bio?.substring(0, 120)}...`}
       />
       {absoluteImageURL && <meta property="og:image" content={absoluteImageURL} />}
       <meta property="og:type" content="profile" />
       <meta property="og:url" content={`https://www.gatesofmusic.com/musicians/${musician.id}`} />

       {/* Données structurées */}
       <script type="application/ld+json">
         {JSON.stringify({
           "@context": "https://schema.org",
           "@type": "MusicGroup",
           "name": musician.name,
           "description": musician.bio,
           "genre": Array.isArray(musician.genres) ? musician.genres.join(', ') : musician.genres,
           "location": {
             "@type": "Place",
             "name": musician.city
           },
           "image": absoluteImageURL,
           "url": `https://www.gatesofmusic.com/musicians/${musician.id}`
         })}
       </script>
     </Helmet>

     <header className="musician-profile-card">
       <img
         src={absoluteImageURL}
         alt={`${musician.name} - Artiste ${musician.genres} à ${musician.city}`}
         className="musician-profile-image"
         loading="lazy"
         width="400"
         height="400"
       />
       <div className="musician-profile-info">
         <h1 className="musician-profile-name">{musician.name}</h1>
        <p>
          <strong>Genre{(musician.genres?.length || 0) > 1 ? 's' : ''} :</strong> {
            musician.genres?.join(', ') || 'Non spécifié'
          }
        </p>
         <p>
           <strong>Type :</strong> {musician.type}
           {musician.performance?.length > 0 && (
             <> ({musician.performance.join(', ')})</>
           )}
         </p>
         <p><strong>Ville :</strong> {musician.city}</p>

         {musician.bio && (
           <section className="musician-profile-bio-block">
             <h2 className="musician-profile-bio-title">À propos</h2>
             <p className="musician-profile-bio-text">{musician.bio}</p>
           </section>
         )}

         {musician.youtubeVideo && (
           <section className="musician-profile-video">
             <h2>Vidéo de présentation</h2>
             <div className="video-wrapper">
               <iframe
                 src={`https://www.youtube.com/embed/${extractYouTubeId(musician.youtubeVideo)}`}
                 title={`Vidéo de présentation de ${musician.name}`}
                 frameBorder="0"
                 allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                 allowFullScreen
                 loading="lazy"
               ></iframe>
             </div>
           </section>
         )}
       </div>
     </header>

     <footer className="musician-profile-bottom">
       <BookingForm entityId={musician.id} entityName={musician.name} />
       <button
         className="musician-profile-back-button"
         onClick={() => navigate('/')}
       >
         Retour à l'accueil
       </button>
     </footer>
   </article>
 );
}
